import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { post_material_enquriy, get_units } from "../../api/ControllApi";
import { ASSET_BASE } from "../../utils/baseUrls";

export default function ProductQuoteDialouge({
  open,
  onClose,
  name,
  id,
  image,
  description,
  userid,
  materialid,
}) {
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [unitId, setUnitId] = useState("");
  const [quote, setQuote] = useState("");

  useEffect(() => {
    if (open) {
      fetchUnits();
    }
  }, [open]);

  const fetchUnits = async () => {
    try {
      const res = await get_units();
      if (res?.status && res?.data) {
        setUnits(res.data);
        // ← Removed auto-select: unitId stays "" so placeholder shows
      }
    } catch (error) {

    }
  };

  const handleQuery = async () => {
    if (!quote) {
      toast.error("Please fill your requirement");
      return;
    }
    const payload = {
      material_id: materialid,
      requirement: quote,
      unit_id: unitId,
      quantity: quantity,
      user_id: userid,
    };

    setLoading(true);
    try {
      const res = await post_material_enquriy(payload);
      if (res?.status === true) {
        toast.success(res?.message || "Enquiry submitted successfully");
        onClose();
      } else {
        toast.error(res?.message || "Something went wrong");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Something went wrong";
      toast.error(msg);

    }
    setLoading(false);
  };

  const getImageUrl = (img) => {
    if (!img) return "/assets/images/placeholder_img.png";
    if (Array.isArray(img)) {
      const firstValid = img.find((url) => url && typeof url === "string");
      if (firstValid) return getImageUrl(firstValid);
      return "/assets/images/placeholder_img.png";
    }
    if (typeof img === "string" && img.includes(",")) {
      return getImageUrl(img.split(",")[0]);
    }
    if (img.startsWith("http")) return img;
    let cleanPath = img.startsWith("/") ? img.substring(1) : img;
    if (cleanPath.startsWith("storage/")) {
      return `${ASSET_BASE}${cleanPath}`;
    }
    return `${ASSET_BASE}storage/${cleanPath}`;
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={(e, reason) => {
        if (reason === "backdropClick") return;
        if (reason === "escapeKeyDown") return;
      }}
      PaperProps={{
        sx: { background: "transparent", boxShadow: "none" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 14,
          right: 14,
          color: "#7E9F40",
          zIndex: 20,
        }}
      >
        <CloseIcon sx={{ fontSize: 28 }} />
      </IconButton>

      <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            bgcolor: "transparent",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* LEFT IMAGE / INFO */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "transparent",
            }}
          >
            <Box
              sx={{
                bgcolor: "#fff",
                p: 3,
                borderRadius: 4,
                boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                width: "100%",
                maxWidth: 320,
              }}
            >
              <Box
                component="img"
                src={getImageUrl(image)}
                sx={{ width: "100%", height: 200, objectFit: "contain", mb: 2 }}
              />
              <Typography variant="h6" fontWeight={700} color="#333">
                {name}
              </Typography>
            </Box>
          </Box>

          {/* RIGHT FORM */}
          <Box sx={{ flex: 1.2, p: 3, bgcolor: "#fff", borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Enter Details
            </Typography>

            {/* QUANTITY & UNITS */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Quantity & Unit
              </Typography>

              <Box display="flex" alignItems="center" gap={2}>
                {/* Quantity Input */}
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ border: "1px solid #ddd", borderRadius: 2, p: 0.5 }}
                >
                  <IconButton
                    size="small"
                    sx={{ color: "#FEB821" }}
                    onClick={() => quantity > 1 && setQuantity((q) => q - 1)}
                  >
                    <RemoveIcon />
                  </IconButton>

                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Qty"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    sx={{
                      width: 50,
                      "& .MuiInput-underline:before, & .MuiInput-underline:after":
                        { display: "none" },
                    }}
                    inputProps={{ style: { textAlign: "center" } }}
                  />

                  <IconButton
                    size="small"
                    sx={{ color: "#FEB821" }}
                    onClick={() => setQuantity((q) => Number(q) + 1)}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {/* Unit Select — no auto-select, shows placeholder until user picks */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={unitId || ""}
                    displayEmpty
                    onChange={(e) => setUnitId(e.target.value)}
                    sx={{ borderRadius: 2 }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: "#aaa" }}>Select unit</span>;
                      }
                      return units.find((u) => u.id === selected)?.name;
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select unit
                    </MenuItem>
                    {units.map((unit) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        {unit.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* REQUIREMENT */}
            <Box mb={4}>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Your Requirement:
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder="Describe your requirement in detail..."
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />
            </Box>

            {/* SUBMIT */}
            <Button
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#FEB821",
                color: "#fff",
                borderRadius: 2,
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#e5a71d" },
              }}
              onClick={handleQuery}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Send Enquiry"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}